/** Share the latest app selection with chat and wait for its view write before sending. */

type ContextState = {
  viewId: string
  value: Record<string, unknown>
  pending: Promise<unknown>
  retry: () => Promise<unknown>
}

const contexts = new Map<string, ContextState>()
const contextKey = (sessionId: string, bindingId: string) =>
  `${sessionId}:${bindingId}`

/** Publish a binding-scoped selection immediately while its server write runs. */
export function publishAppContext(
  sessionId: string,
  bindingId: string,
  viewId: string,
  value: Record<string, unknown> | null,
  pending?: Promise<unknown>,
  retry?: () => Promise<unknown>,
) {
  const key = contextKey(sessionId, bindingId)
  if (value && pending && retry)
    contexts.set(key, { viewId, value, pending, retry })
  else if (contexts.get(key)?.viewId === viewId) contexts.delete(key)
  else return
  window.dispatchEvent(
    new CustomEvent('bridge:app-context-changed', {
      detail: { sessionId, bindingId, context: value },
    }),
  )
}

/** Restore chips when the composer mounts after the app pane updated context. */
export function currentAppContext(sessionId: string, bindingId: string) {
  return contexts.get(contextKey(sessionId, bindingId))?.value ?? null
}

/** Let an in-flight guest update arrive, then wait for the newest accepted view context. */
export async function waitForAppContext(sessionId: string, bindingId: string) {
  const key = contextKey(sessionId, bindingId)
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  let retried = false
  while (true) {
    const state = contexts.get(key)
    if (!state) return
    try {
      await state.pending
    } catch (failure) {
      if (contexts.get(key) !== state) continue
      if (retried) throw failure
      retried = true
      await state.retry()
      continue
    }
    if (contexts.get(key) === state) return
  }
}
